import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DesignRenderer from './DesignRenderer';

/**
 * PresentMode — fullscreen presentation overlay.
 *
 * Renders design files as slides on a dark backdrop. Each entry in `slides`
 * is an array of files (same shape as filesToRender) representing one artboard.
 *
 * Navigation:
 *   - Arrow keys Left/Right → prev/next slide
 *   - ESC → exit
 *   - Click left third → prev, click right third → next
 *
 * Cursor auto-hides after 3s of inactivity.
 */

const ARTBOARD_W = 1200;
const ARTBOARD_H = 800;

// Scale the artboard to fit the viewport with some padding
function computeScale(vpW, vpH) {
  const scaleX = (vpW * 0.9) / ARTBOARD_W;
  const scaleY = (vpH * 0.88) / ARTBOARD_H;
  return Math.min(scaleX, scaleY, 1.5); // cap at 1.5× for huge screens
}

const PresentMode = ({ slides = [], initialIndex = 0, onClose }) => {
  const [index, setIndex] = useState(Math.max(0, Math.min(initialIndex, slides.length - 1)));
  const [transitioning, setTransitioning] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);
  const [vpSize, setVpSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const cursorTimerRef = useRef(null);

  const total = slides.length || 1;
  const clampedIndex = Math.max(0, Math.min(index, total - 1));
  const currentFiles = slides[clampedIndex] || [];
  const scale = computeScale(vpSize.w, vpSize.h);

  // ── viewport resize ──────────────────────────────────────────────────────────

  useEffect(() => {
    const onResize = () => setVpSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── navigation ───────────────────────────────────────────────────────────────

  const goTo = useCallback((newIndex) => {
    if (transitioning) return;
    const clamped = Math.max(0, Math.min(newIndex, total - 1));
    setTransitioning(true);
    setTimeout(() => {
      setIndex(clamped);
      setTransitioning(false);
    }, 150);
  }, [transitioning, total]);

  const prev = useCallback(() => goTo(clampedIndex - 1), [goTo, clampedIndex]);
  const next = useCallback(() => goTo(clampedIndex + 1), [goTo, clampedIndex]);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next();
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') prev();
      else if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, onClose]);

  // ── cursor auto-hide ──────────────────────────────────────────────────────────

  const resetCursorTimer = useCallback(() => {
    setCursorVisible(true);
    clearTimeout(cursorTimerRef.current);
    cursorTimerRef.current = setTimeout(() => setCursorVisible(false), 3000);
  }, []);

  useEffect(() => {
    resetCursorTimer();
    return () => clearTimeout(cursorTimerRef.current);
  }, [resetCursorTimer]);

  // ── click zones ───────────────────────────────────────────────────────────────

  const handleBackdropClick = useCallback((e) => {
    const x = e.clientX / vpSize.w;
    if (x < 0.33) prev();
    else if (x > 0.67) next();
  }, [vpSize.w, prev, next]);

  // ── render (as portal so it sits above everything) ────────────────────────────

  const content = (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: cursorVisible ? 'default' : 'none',
      }}
      onMouseMove={resetCursorTimer}
      onClick={handleBackdropClick}
    >
      {/* Close button */}
      <IconButton
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          color: 'rgba(255,255,255,0.7)',
          background: 'rgba(255,255,255,0.08)',
          '&:hover': { background: 'rgba(255,255,255,0.18)', color: '#fff' },
          zIndex: 10000,
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Artboard */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 32px 96px rgba(0,0,0,0.6)',
          opacity: transitioning ? 0 : 1,
          transition: 'opacity 0.15s ease-in-out',
          flexShrink: 0,
          width: ARTBOARD_W,
          height: ARTBOARD_H,
          background: '#fff',
        }}
      >
        {currentFiles.length > 0 && (
          <DesignRenderer
            files={currentFiles}
            width={ARTBOARD_W}
            height={ARTBOARD_H}
            editMode={false}
            commentMode={false}
          />
        )}
      </Box>

      {/* Slide counter */}
      {total > 1 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {/* Dot nav */}
          {slides.map((_, i) => (
            <Box
              key={i}
              onClick={(e) => { e.stopPropagation(); goTo(i); }}
              sx={{
                width: i === clampedIndex ? 20 : 6,
                height: 6,
                borderRadius: 3,
                background: i === clampedIndex
                  ? 'rgba(255,255,255,0.9)'
                  : 'rgba(255,255,255,0.3)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            />
          ))}
        </Box>
      )}

      {/* Counter text */}
      <Typography
        variant="caption"
        sx={{
          position: 'fixed',
          bottom: 40,
          right: 24,
          color: 'rgba(255,255,255,0.4)',
          fontSize: 12,
          fontFamily: 'monospace',
        }}
      >
        {clampedIndex + 1} / {total}
      </Typography>

      {/* Click zone hints — very subtle */}
      {cursorVisible && total > 1 && (
        <>
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 48,
              height: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.3)',
              fontSize: 24,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            ‹
          </Box>
          <Box
            sx={{
              position: 'fixed',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 48,
              height: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.3)',
              fontSize: 24,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            ›
          </Box>
        </>
      )}
    </Box>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default PresentMode;
